import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import MDSpinner from "react-md-spinner";
import { useSelector } from "react-redux";

import { useOrganisationContract } from "../hooks/organisation";
import RainIcon from "../icons/rain_icon.svg?react";
import { checkPermission } from "../state/appState";
import { getSelectedOrganisation } from "../state/simulation";
import ErrorMessageStyles from "../styles/ErrorMessage.module.css";
import FormStyles from "../styles/Forms.module.css";
import inputElementStyles from "../styles/InputElements.module.css";
import { formattedMessageForErrorCode } from "../utils/authorizationUtils";

import CollapsableSidebarStyles from "./CollapsableSidebar.module.css";
import ExistingConstantRain from "./existing/ExistingConstantRain";
import ExistingLizardRasterRain from "./existing/ExistingLizardRasterRain";
import ExistingTimeseriesRain from "./existing/ExistingTimeseriesRain";
import ExistingItemsContainer from "./ExistingItemsContainer";
import { FormLabel } from "./FormLabel";
import LeftMenuHeader from "./LeftMenuHeader";
import menuStyles from "./Menu.module.css";
import RainMenuCustom from "./RainMenuCustom";
import RainMenuDesign from "./RainMenuDesign";
import RainMenuRaster from "./RainMenuRaster";

type RainType = "CUSTOM" | "RADAR" | "FORECAST" | "DESIGN";

const RainMenu = () => {
  const [rainType, setRainType] = useState<RainType>("CUSTOM");
  const intl = useIntl();

  const permission = useSelector(checkPermission)("editRain");

  const disableWholeForm = permission.allowed === false;
  const disablesWholeFormErrorMessage =
    permission.allowed === false && formattedMessageForErrorCode(permission.errorCode, intl);

  const selectBoxTypeConstant = intl.formatMessage({
    id: "newRainMenu.raintypeConstant",
    defaultMessage: "Constant",
  });
  const selectBoxTypeRadar = intl.formatMessage({
    id: "newRainMenu.raintypeRadar",
    defaultMessage: "Radar",
  });
  const selectBoxTypeForecast = intl.formatMessage({
    id: "newRainMenu.raintypeForecast",
    defaultMessage: "Forecast",
  });
  const selectBoxTypeDesign = intl.formatMessage({
    id: "newRainMenu.raintypeDesign",
    defaultMessage: "Design",
  });

  const organisation = useSelector(getSelectedOrganisation);

  const contract = useOrganisationContract(organisation ? organisation.uniqueId : "");

  if (!contract) {
    return <MDSpinner />;
  }

  const hasLizardrainScope = contract?.scope?.includes("lizardrain");

  return (
    <div className={`${CollapsableSidebarStyles.LeftMenu}`}>
      <LeftMenuHeader Icon={RainIcon}>
        <FormattedMessage id="rainMenu.header" defaultMessage="Rain" />
      </LeftMenuHeader>
      <div
        className={`${menuStyles.SubMenu} ${menuStyles.SubMenuStaticContent} ${menuStyles.List}`}
        style={{ padding: "0 8px 0 8px" }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
          className={FormStyles.NormalForm}
        >
          <fieldset disabled={disableWholeForm}>
            <FormLabel>
              <FormattedMessage id="newRainMenu.type_label" defaultMessage="Type" />
            </FormLabel>
            <select
              className={`${inputElementStyles.Padding} ${inputElementStyles.Input}`}
              value={rainType}
              onChange={(e) => {
                const value = e.target.value;
                let raintype: RainType = "CUSTOM";
                if (value === "CUSTOM") raintype = "CUSTOM";
                if (value === "RADAR") raintype = "RADAR";
                if (value === "FORECAST") raintype = "FORECAST";
                if (value === "DESIGN") raintype = "DESIGN";
                setRainType(raintype);
              }}
            >
              <option value={"CUSTOM"}>{selectBoxTypeConstant}</option>
              <option
                value={"RADAR"}
                title={
                  !hasLizardrainScope
                    ? intl.formatMessage({
                        id: "error.not_sufficient_right",
                        defaultMessage:
                          "You do not have sufficient right to perform this request. Please contact the Service Desk for more information.",
                      })
                    : ""
                }
                disabled={!hasLizardrainScope}
              >
                {selectBoxTypeRadar}
              </option>
              <option
                value={"FORECAST"}
                title={
                  !hasLizardrainScope
                    ? intl.formatMessage({
                        id: "error.not_sufficient_right",
                        defaultMessage:
                          "You do not have sufficient right to perform this request. Please contact the Service Desk for more information.",
                      })
                    : ""
                }
                disabled={!hasLizardrainScope}
              >
                {selectBoxTypeForecast}
              </option>
              <option value={"DESIGN"}>{selectBoxTypeDesign}</option>
            </select>

            {rainType === "CUSTOM" ? <RainMenuCustom /> : null}
            {rainType === "RADAR" ? <RainMenuRaster type="radar" /> : null}
            {rainType === "FORECAST" ? <RainMenuRaster type="forecast" /> : null}
            {rainType === "DESIGN" ? <RainMenuDesign /> : null}
            <div className={`${menuStyles.ListItem} `}>
              {disablesWholeFormErrorMessage ? (
                <span className={ErrorMessageStyles.ErrorMessageStyle}>
                  {disablesWholeFormErrorMessage}
                </span>
              ) : null}
            </div>
          </fieldset>
        </form>
      </div>
      <ExistingItemsContainer>
        <ExistingConstantRain />
        <ExistingLizardRasterRain />
        <ExistingTimeseriesRain />
      </ExistingItemsContainer>
    </div>
  );
};

export default RainMenu;
